import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Download,
  Loader2,
  FileText,
  Calendar,
  DollarSign,
  Users,
  ExternalLink,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import type { Tables } from "@/integrations/supabase/types";
import { getTypeConfig, getFamilyFromCategory, getFamilyConfig } from "@/constants/ui-mapping";
import { SmartDocumentViewer } from "@/components/viewer/SmartDocumentViewer";
import { MobileDocumentViewer } from "./MobileDocumentViewer";
import { useIsMobile } from "@/hooks/use-mobile";

type DocumentType = Tables<"documents">;

interface DocumentPreviewSheetProps {
  document: DocumentType | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DocumentPreviewSheet({ document, open, onOpenChange }: DocumentPreviewSheetProps) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  // Parse key_fields safely
  const keyFields = document?.key_fields as Record<string, any> | null;

  // Fetch file URL and update last_viewed_at when document changes
  useEffect(() => {
    if (!document || !open) {
      if (!open) {
        setFileUrl(null);
      }
      return;
    }

    const fetchFile = async () => {
      setIsFetching(true);

      // Use document.bucket_id if available, default to "documents"
      const bucketId = (document as any).bucket_id || "documents";
      
      const { data, error } = await supabase.storage
        .from(bucketId)
        .createSignedUrl(document.file_path, 300);

      if (error) {
        console.error("Error fetching file:", error);
        setFileUrl(null);
      } else {
        setFileUrl(data.signedUrl);
      }
      setIsFetching(false);
    };

    // Update last_viewed_at when opening preview
    const updateLastViewed = async () => {
      await supabase
        .from("documents")
        .update({ last_viewed_at: new Date().toISOString() })
        .eq("id", document.id);
    };

    fetchFile();
    updateLastViewed();
  }, [document?.id, open]);

  const handleDownload = async () => {
    if (!document || !fileUrl) return;

    const link = window.document.createElement("a");
    link.href = fileUrl;
    link.download = document.file_name;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  if (!document) return null;

  const typeConfig = getTypeConfig(document.doc_type);
  
  // Utiliser le système de famille (cohérent avec DocumentDetail)
  const displayFamily = getFamilyFromCategory(document.category);
  const familyConfig = getFamilyConfig(displayFamily);

  // Use mobile viewer on mobile devices
  if (isMobile) {
    return (
      <MobileDocumentViewer
        document={document}
        open={open}
        onClose={() => onOpenChange(false)}
      />
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col">
        {/* Header */}
        <SheetHeader className="px-4 py-3 border-b shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center text-xl shrink-0">
              {typeConfig.emoji}
            </div>
            <div className="flex-1 min-w-0">
              <SheetTitle className="text-sm font-semibold truncate pr-8">
                {document.title}
              </SheetTitle>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5">
                  {typeConfig.label}
                </Badge>
                <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full", familyConfig.color)}>
                  {familyConfig.emoji} {familyConfig.label}
                </span>
              </div>
            </div>
          </div>
        </SheetHeader>

        {/* Content - Full height layout without double scroll */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Metadata section - scrollable if needed, max height */}
          <ScrollArea className="shrink-0 max-h-32">
            <div className="px-4 py-3 space-y-3">
              {/* Quick info */}
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {document.doc_date && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(document.doc_date)}</span>
                  </div>
                )}
                {keyFields?.amount && (
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <DollarSign className="w-4 h-4 text-primary" />
                    <span>{keyFields.amount} {keyFields.currency || "€"}</span>
                  </div>
                )}
                {document.issuer_name && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Users className="w-4 h-4" />
                    <span className="truncate">{document.issuer_name}</span>
                  </div>
                )}
              </div>

              {/* AI Summary */}
              {document.ai_summary && (
                <div className="p-2 rounded-lg bg-primary/5 border border-primary/10">
                  <p className="text-xs text-muted-foreground mb-0.5 font-medium">Résumé IA</p>
                  <p className="text-sm leading-relaxed line-clamp-2">{document.ai_summary}</p>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Document viewer - takes all remaining space */}
          <div className="flex-1 overflow-hidden border-t">
            {isFetching ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : !fileUrl ? (
              <div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground">
                <FileText className="w-10 h-10" />
                <p className="text-sm">Aperçu non disponible</p>
              </div>
            ) : (
              <SmartDocumentViewer
                url={fileUrl}
                title={document.title}
                mimeType={document.mime_type || 'application/octet-stream'}
                className="h-full"
                showControls
              />
            )}
          </div>
        </div>

        {/* Footer actions */}
        <div className="px-4 py-3 border-t flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={handleDownload}
            disabled={!fileUrl}
          >
            <Download className="w-4 h-4 mr-2" />
            Télécharger
          </Button>
          <Button
            size="sm"
            className="flex-1"
            onClick={() => {
              onOpenChange(false);
              navigate(`/documents/${document.id}`);
            }}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Ouvrir
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
